import React, { useState, useMemo } from 'react';
import { localName } from './AttractionCard';
import { byRankScore } from '../utils/rank';

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const CAT_ICON = { culture:'🏛️', nature:'🌿', play:'🎡', shopping:'🛍️', food:'🍜' };
const SPOT_CATS = ['culture', 'nature', 'play', 'shopping'];

// 景點大分類的顯示名稱
const SPOT_CAT_LABEL = {
  zh:      { culture:'文化', nature:'自然', play:'玩樂', shopping:'購物' },
  'zh-CN': { culture:'文化', nature:'自然', play:'玩乐', shopping:'购物' },
  en:      { culture:'Culture', nature:'Nature', play:'Fun', shopping:'Shopping' },
  ja:      { culture:'文化', nature:'自然', play:'遊び', shopping:'買い物' },
  ko:      { culture:'문화', nature:'자연', play:'놀거리', shopping:'쇼핑' },
};

// 美食細分類（specify_cat）的短名與圖示 — 原字串太長，放不進標籤
// 順序：[圖示, 繁中, 簡中, 英, 日, 韓]
const FOOD_CAT = {
  '特色餐廳/在地美食':            ['🍽️','在地美食','在地美食','Local','郷土料理','현지 맛집'],
  '日式料理/壽司':                ['🍣','壽司','寿司','Sushi','寿司','스시'],
  '日式料理/居酒屋':              ['🏮','居酒屋','居酒屋','Izakaya','居酒屋','이자카야'],
  '日式料理/燒肉/鐵板燒/壽喜燒':  ['🥩','燒肉','烧肉','Yakiniku','焼肉','야키니쿠'],
  '中式料理/火鍋/粵菜':           ['🥟','中菜','中餐','Chinese','中華','중식'],
  '中式料理/火鍋/大排檔':         ['🍲','大排檔','大排档','Dai Pai Dong','屋台','포장마차'],
  '麵食/餃子/小籠包':             ['🍜','麵食','面食','Noodles','麺類','면 요리'],
  '韓式料理':                     ['🥘','韓式','韩式','Korean','韓国料理','한식'],
  '東南亞料理/泰式/越南':         ['🍛','東南亞','东南亚','SE Asian','東南アジア','동남아'],
  '印度料理/南亞料理':            ['🫓','印度','印度','Indian','インド','인도'],
  '法式料理/精緻餐廳':            ['🥂','法式','法式','French','フレンチ','프렌치'],
  '義式料理/Pizza/Pasta':         ['🍕','意式','意式','Italian','イタリアン','이탈리안'],
  '地中海料理/西班牙料理':        ['🥘','地中海','地中海','Mediterranean','地中海','지중해'],
  '中東料理/土耳其料理':          ['🧆','中東','中东','Middle Eastern','中東','중동'],
  '墨西哥料理/拉丁美洲料理':      ['🌮','墨西哥','墨西哥','Mexican','メキシカン','멕시칸'],
  '美式料理/漢堡/BBQ':            ['🍔','美式','美式','American','アメリカン','아메리칸'],
  '咖啡廳/甜點/下午茶':           ['☕','咖啡甜點','咖啡甜点','Cafés','カフェ','카페'],
  '酒吧/餐酒館/夜生活':           ['🍸','酒吧','酒吧','Bars','バー','바'],
  '素食/蔬食/健康餐':             ['🥗','素食','素食','Vegetarian','ベジタリアン','채식'],
  'Halal/清真餐廳':               ['🕌','清真','清真','Halal','ハラル','할랄'],
};
const FOOD_LANG_IDX = { zh:1, 'zh-CN':2, en:3, ja:4, ko:5 };

// Takes the best `n` items from an already-sorted array, preferring ones with
// a real photo first so the most visible "recommended" section doesn't lead
// with imageless cards — only backfills from imageless items if too few
// photographed ones exist.
function takePreferImaged(sorted, n = 6) {
  const imaged = sorted.filter(a => a.imageUrl);
  const unimaged = sorted.filter(a => !a.imageUrl);
  return [...imaged, ...unimaged].slice(0, n);
}

// 中位數 — 沒開定位時用景點座標的中位數當「中心點」（同行程生成的 cityCenter 做法），
// 首頁選了分區時傳進來的就只有該區景點，算出來即是分區中心
function median(arr) {
  const s = [...arr].sort((a, b) => a - b);
  return s.length ? s[Math.floor(s.length / 2)] : null;
}

const LABELS = {
  zh:  { title:'📍 附近推薦', spots:'附近景點', food:'附近美食', all:'全部', fromCenter:'距離以此區中心估算 · 開啟定位更準', locating:'定位中…', btn:'開啟定位', noGps:'未開啟', m:'m', km:'km', away:'外' },
  en:  { title:'📍 Nearby', spots:'Nearby Spots', food:'Nearby Food', all:'All', fromCenter:'Distances from the area centre · enable GPS for accuracy', locating:'Locating…', btn:'Enable GPS', noGps:'No GPS', m:'m', km:'km', away:' away' },
  ja:  { title:'📍 近くのおすすめ', spots:'近くのスポット', food:'近くのグルメ', all:'すべて', fromCenter:'距離はエリア中心からの目安 · 位置情報でより正確に', locating:'取得中…', btn:'位置情報を許可', noGps:'GPS無効', m:'m', km:'km', away:'' },
  ko:  { title:'📍 근처 추천', spots:'근처 명소', food:'근처 맛집', all:'전체', fromCenter:'지역 중심 기준 거리 · 위치를 켜면 더 정확해요', locating:'위치 확인 중…', btn:'위치 허용', noGps:'GPS 없음', m:'m', km:'km', away:'' },
};

function DistLabel({ dist, L }) {
  if (dist == null) return null;
  const text = dist < 1
    ? `${Math.round(dist * 1000)}${L.m}`
    : `${dist.toFixed(1)}${L.km}`;
  return (
    <span style={{ background:'#0369a1', color:'white', borderRadius:8, padding:'1px 6px', fontSize:9, fontWeight:900, flexShrink:0 }}>
      {text}
    </span>
  );
}

function MiniCard({ a, dist, onSelect, L, lang, tag }) {
  const icon = CAT_ICON[a.category] || '📍';
  const displayName = localName(a, lang);
  // Scraped Google Photos URLs (lh3.googleusercontent.com) expire, so imgFailed
  // is common — must be React state, not direct DOM mutation (a prior version
  // set e.target.parentNode.innerHTML on error, which desyncs React's view of
  // the DOM and throws "removeChild" errors — and a full white-screen crash —
  // the next time this list re-renders, e.g. after granting GPS permission).
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = a.imageUrl && !imgFailed;
  return (
    <div onClick={() => onSelect && onSelect(a)}
      style={{ flexShrink:0, width:110, cursor:'pointer', borderRadius:12, overflow:'hidden',
        background:'white', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', border:'1px solid #f1f5f9' }}>
      {/* Image / emoji header */}
      <div style={{ height:64, background: showImg ? 'transparent' : '#f8fafc',
        display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden' }}>
        {showImg
          ? <img src={a.imageUrl} alt={displayName} style={{ width:'100%', height:'100%', objectFit:'cover' }}
              onError={() => setImgFailed(true)}/>
          : <span style={{ fontSize:28 }}>{icon}</span>
        }
        {tag && (
          <div style={{ position:'absolute', top:4, left:4, background:'rgba(15,23,42,0.65)', color:'white', borderRadius:6, padding:'1px 5px', fontSize:8.5, fontWeight:700, maxWidth:96, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {tag}
          </div>
        )}
        {dist != null && (
          <div style={{ position:'absolute', bottom:4, right:4 }}>
            <DistLabel dist={dist} L={L}/>
          </div>
        )}
        {dist == null && a.rating && (
          <div style={{ position:'absolute', bottom:4, right:4, background:'#f59e0b', color:'#0f172a', borderRadius:8, padding:'1px 6px', fontSize:9, fontWeight:900 }}>
            ⭐{a.rating}
          </div>
        )}
      </div>
      {/* Name */}
      <div style={{ padding:'6px 8px' }}>
        <p style={{ margin:0, fontSize:11, fontWeight:700, color:'#1e293b', lineHeight:1.3,
          overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {displayName}
        </p>
      </div>
    </div>
  );
}

function Row({ items, onSelect, L, lang, tagOf }) {
  if (!items.length) return null;
  return (
    <div style={{ display:'flex', gap:10, overflowX:'auto', paddingBottom:4,
      scrollbarWidth:'none', msOverflowStyle:'none' }}>
      {items.map(a => (
        <MiniCard key={a.place_id || a.name} a={a} dist={a._dist} onSelect={onSelect} L={L} lang={lang} tag={tagOf(a)}/>
      ))}
    </div>
  );
}

// 分類標籤列；只有一個分類時不顯示（沒得選）
function Chips({ opts, value, onChange, L }) {
  if (opts.length < 2) return null;
  const all = [{ key:'all', label:L.all }, ...opts];
  return (
    <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:8, scrollbarWidth:'none', msOverflowStyle:'none' }}>
      {all.map(o => {
        const on = o.key === value;
        return (
          <button key={o.key} onClick={() => onChange(o.key)}
            style={{ flexShrink:0, border:'none', borderRadius:999, padding:'4px 10px', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
              background: on ? '#0f172a' : '#f1f5f9', color: on ? 'white' : '#64748b' }}>
            {o.label}{o.n != null && <span style={{ opacity:0.6, marginLeft:3, fontWeight:600 }}>{o.n}</span>}
          </button>
        );
      })}
    </div>
  );
}

// gps/status/onRequestGps come from the shared useGeolocation() hook (see
// src/hooks/useGeolocation.js) lifted up to the App component — the home
// page's auto-detect and this card's manual "開啟定位" fallback button now
// share one permission request and one coordinate, instead of each running
// its own independent navigator.geolocation call.
export function RecommendCard({ attractions, lang, onSelect, gps, status, onRequestGps }) {
  const langKey = lang === 'zh-CN' ? 'zh' : lang === 'en' ? 'en' : lang === 'ja' ? 'ja' : lang === 'ko' ? 'ko' : 'zh';
  const L = LABELS[langKey] || LABELS.zh;
  const catLang = lang === 'zh-CN' ? 'zh-CN' : langKey;
  const [spotCat, setSpotCat] = useState('all');
  const [foodCat, setFoodCat] = useState('all');

  const hasGps = status === 'ok' && gps;

  // 排好序的候選池：有座標的按距離（GPS 或中心點），完全沒座標時退回評分排序
  const { spotPool, foodPool } = useMemo(() => {
    const withCoords = attractions.filter(a => a.lat != null && a.lng != null);
    let origin = hasGps ? gps : null;
    if (!origin && withCoords.length) {
      origin = { lat: median(withCoords.map(a => a.lat)), lng: median(withCoords.map(a => a.lng)) };
    }
    const sorted = origin
      ? withCoords
          .map(a => ({ ...a, _dist: haversine(origin.lat, origin.lng, a.lat, a.lng) }))
          .sort((a, b) => a._dist - b._dist)
      : [...attractions].sort(byRankScore);
    return {
      spotPool: sorted.filter(a => a.category !== 'food'),
      foodPool: sorted.filter(a => a.category === 'food'),
    };
  }, [hasGps, gps, attractions]);

  const foodKey = a => (FOOD_CAT[a.specify_cat] ? a.specify_cat : null);

  const spotOpts = useMemo(() => SPOT_CATS
    .map(k => ({ key:k, label:`${CAT_ICON[k]} ${SPOT_CAT_LABEL[catLang][k]}`, n: spotPool.filter(a => a.category === k).length }))
    .filter(o => o.n > 0), [spotPool, catLang]);

  const foodOpts = useMemo(() => {
    const counts = {};
    foodPool.forEach(a => { const k = foodKey(a); if (k) counts[k] = (counts[k] || 0) + 1; });
    return Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .map(k => ({ key:k, label:`${FOOD_CAT[k][0]} ${FOOD_CAT[k][FOOD_LANG_IDX[catLang]]}`, n: counts[k] }));
  }, [foodPool, catLang]);

  // 換城市/分區後原本選的分類可能不存在，退回「全部」
  const activeSpot = spotCat !== 'all' && spotOpts.some(o => o.key === spotCat) ? spotCat : 'all';
  const activeFood = foodCat !== 'all' && foodOpts.some(o => o.key === foodCat) ? foodCat : 'all';

  const spots    = takePreferImaged(activeSpot === 'all' ? spotPool : spotPool.filter(a => a.category === activeSpot));
  const foodList = takePreferImaged(activeFood === 'all' ? foodPool : foodPool.filter(a => foodKey(a) === activeFood));

  const spotTag = a => SPOT_CAT_LABEL[catLang][a.category] || null;
  const foodTag = a => { const k = foodKey(a); return k ? FOOD_CAT[k][FOOD_LANG_IDX[catLang]] : null; };

  if (!spotPool.length && !foodPool.length) return null;
  const usingCenter = !hasGps && spots.concat(foodList).some(a => a._dist != null);

  return (
    <div style={{ background:'white', borderRadius:16, padding:'14px 16px', marginBottom:20,
      boxShadow:'0 2px 12px rgba(0,0,0,0.07)', border:'1px solid #f1f5f9' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: usingCenter ? 8 : 14 }}>
        <p style={{ margin:0, fontSize:15, fontWeight:900, color:'#0f172a' }}>{L.title}</p>
        {(status === 'idle' || status === 'denied' || status === 'unsupported') && (
          <button onClick={onRequestGps}
            style={{ fontSize:10, color:'#64748b', fontWeight:700, background:'#f1f5f9', border:'none', borderRadius:8, padding:'4px 10px', cursor:'pointer' }}>
            📍 {status === 'denied' ? L.noGps : L.btn}
          </button>
        )}
        {status === 'asking' && (
          <span style={{ fontSize:10, color:'#f59e0b', fontWeight:700 }}>{L.locating}</span>
        )}
        {hasGps && (
          <span style={{ fontSize:10, color:'#4ade80', fontWeight:700 }}>● GPS</span>
        )}
      </div>
      {usingCenter && (
        <p style={{ margin:'0 0 12px', fontSize:10.5, color:'#64748b', background:'#f8fafc', borderRadius:8, padding:'5px 8px' }}>
          {L.fromCenter}
        </p>
      )}

      {/* Spots row */}
      {spotPool.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <p style={{ margin:'0 0 6px', fontSize:12, fontWeight:700, color:'#64748b' }}>{L.spots}</p>
          <Chips opts={spotOpts} value={activeSpot} onChange={setSpotCat} L={L}/>
          <Row items={spots} onSelect={onSelect} L={L} lang={lang} tagOf={spotTag}/>
        </div>
      )}

      {/* Food row */}
      {foodPool.length > 0 && (
        <div>
          <p style={{ margin:'0 0 6px', fontSize:12, fontWeight:700, color:'#64748b' }}>{L.food}</p>
          <Chips opts={foodOpts} value={activeFood} onChange={setFoodCat} L={L}/>
          <Row items={foodList} onSelect={onSelect} L={L} lang={lang} tagOf={foodTag}/>
        </div>
      )}
    </div>
  );
}
